import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { ArrowLeft, Check, Loader2, Store } from "lucide-react";
import Backdrop from "./Backdrop";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CITIES = [
  { ar: "الرياض", en: "Riyadh" },
  { ar: "جدة", en: "Jeddah" },
  { ar: "الخبر", en: "Khobar" },
  { ar: "الدمام", en: "Dammam" },
  { ar: "العُلا", en: "AlUla" },
  { ar: "المدينة المنورة", en: "Madinah" },
  { ar: "مكة المكرمة", en: "Makkah" },
  { ar: "أبها", en: "Abha" },
  { ar: "الطائف", en: "Taif" },
  { ar: "تبوك", en: "Tabuk" },
  { ar: "حائل", en: "Hail" },
  { ar: "نيوم", en: "NEOM" },
];

const EXPERIENCE_TYPES = [
  { ar: "مطعم / تجربة طعام", en: "Restaurant / Dining" },
  { ar: "فندق / إقامة فاخرة", en: "Hotel / Luxury Stay" },
  { ar: "نشاط مغامرة", en: "Adventure Activity" },
  { ar: "جولة ثقافية", en: "Cultural Tour" },
  { ar: "تجربة صحراوية", en: "Desert Experience" },
  { ar: "رحلة بحرية / يخت", en: "Yacht / Marine Trip" },
  { ar: "سبا / استرخاء", en: "Spa / Wellness" },
  { ar: "تنقّلات فاخرة", en: "Premium Transport" },
  { ar: "تسوّق", en: "Shopping" },
  { ar: "ترفيه / حفلات", en: "Entertainment / Events" },
  { ar: "أخرى", en: "Other" },
];

export default function VendorRegister() {
  const { isAr, lang, toggle } = useLang();
  const [form, setForm] = useState({
    vendor_name: "",
    city: "",
    experience_type: "",
    phone: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [vendors, setVendors] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const fetchVendors = async () => {
    try {
      const res = await fetch(`${BACKEND}/api/vendors`);
      const data = await res.json();
      setVendors(Array.isArray(data) ? data : []);
    } catch {
      setVendors([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.vendor_name.trim() || !form.city || !form.experience_type || !form.phone.trim()) {
      setError(isAr ? "الرجاء تعبئة كل الحقول المطلوبة." : "Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/vendors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed");
      setDone(true);
      setForm({ vendor_name: "", city: "", experience_type: "", phone: "", description: "" });
      fetchVendors();
      setTimeout(() => setDone(false), 3500);
    } catch (err) {
      setError(isAr ? "حدث خطأ. حاول مرة أخرى." : "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-x-hidden grain" data-testid="vendor-register-page">
      <Backdrop />

      <header className="relative z-20 mx-auto max-w-7xl px-5 pt-8 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-emerald-deep hover:text-emerald transition" data-testid="vendor-back-home">
          <ArrowLeft size={18} className={isAr ? "rotate-180" : ""} />
          <span style={{ fontFamily: isAr ? "Cairo" : "Plus Jakarta Sans" }} className="font-bold text-sm">
            {isAr ? "العودة للرئيسية" : "Back to home"}
          </span>
        </Link>
        <button
          onClick={toggle}
          data-testid="vendor-lang-toggle"
          className="rounded-full border-2 border-emerald/60 bg-cream-light/60 px-3.5 py-1.5 text-xs font-bold text-emerald-deep hover:bg-emerald hover:text-cream transition"
        >
          <span className="font-display-en">{lang === "ar" ? "EN" : "عربي"}</span>
        </button>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-5 pt-10 pb-20">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs md:text-sm font-medium text-emerald-deep mb-6">
            <Store size={14} className="text-gold-deep" />
            <span style={{ fontFamily: isAr ? "Cairo" : "Plus Jakarta Sans" }}>
              {isAr ? "بوابة الشركاء" : "Partner Portal"}
            </span>
          </div>
          <h1
            className={`${isAr ? "font-display-ar" : "font-display-en"} text-emerald-deep text-4xl sm:text-5xl mb-4`}
            style={{ lineHeight: 1.25 }}
          >
            {isAr ? "تسجيل المورّدين" : "Vendor Registration"}
          </h1>
          <p
            className="text-emerald-deep/70 text-base max-w-xl mx-auto"
            style={{ fontFamily: isAr ? "Cairo" : "Plus Jakarta Sans" }}
          >
            {isAr
              ? "أضف بيانات المورّد لتدريب وكيل دانة الذكي على ترشيح تجارب حقيقية."
              : "Add a vendor's info to train the Dana AI agent on real local experiences."}
          </p>
        </div>

        <form
          onSubmit={submit}
          className="glass rounded-[2rem] p-6 md:p-10 space-y-5"
          data-testid="vendor-form"
        >
          <Field label={isAr ? "اسم المورّد *" : "Vendor name *"}>
            <input
              data-testid="vendor-name-input"
              value={form.vendor_name}
              onChange={onChange("vendor_name")}
              className="form-input"
              placeholder={isAr ? "مثلاً: مطعم النخبة" : "e.g. Elite Restaurant"}
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label={isAr ? "المدينة *" : "City *"}>
              <select
                data-testid="vendor-city-select"
                value={form.city}
                onChange={onChange("city")}
                className="form-input"
              >
                <option value="">{isAr ? "اختر المدينة" : "Select city"}</option>
                {CITIES.map((c) => (
                  <option key={c.en} value={c.en}>{isAr ? c.ar : c.en}</option>
                ))}
              </select>
            </Field>

            <Field label={isAr ? "نوع التجربة *" : "Experience type *"}>
              <select
                data-testid="vendor-type-select"
                value={form.experience_type}
                onChange={onChange("experience_type")}
                className="form-input"
              >
                <option value="">{isAr ? "اختر النوع" : "Select type"}</option>
                {EXPERIENCE_TYPES.map((c) => (
                  <option key={c.en} value={c.en}>{isAr ? c.ar : c.en}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label={isAr ? "رقم الجوال (واتساب) *" : "Phone (WhatsApp) *"}>
            <input
              data-testid="vendor-phone-input"
              value={form.phone}
              onChange={onChange("phone")}
              className="form-input"
              placeholder="+9665XXXXXXXX"
              dir="ltr"
            />
          </Field>

          <Field label={isAr ? "وصف مختصر (اختياري)" : "Short description (optional)"}>
            <textarea
              data-testid="vendor-desc-input"
              value={form.description}
              onChange={onChange("description")}
              rows={3}
              className="form-input resize-none"
              placeholder={isAr ? "تفاصيل التجربة، الأسعار، أوقات العمل…" : "Experience details, pricing, hours…"}
            />
          </Field>

          {error && (
            <div className="text-rose-700 text-sm bg-rose-50 border border-rose-200 rounded-xl px-4 py-2.5" data-testid="vendor-error">
              {error}
            </div>
          )}

          {done && (
            <div className="text-emerald-deep text-sm bg-emerald-50 border border-emerald/30 rounded-xl px-4 py-2.5 flex items-center gap-2" data-testid="vendor-success">
              <Check size={16} className="text-emerald" />
              {isAr ? "تم التسجيل بنجاح! المورّد أُضيف لقاعدة البيانات." : "Saved! Vendor added to the database."}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="vendor-submit-btn"
            className="btn-primary w-full justify-center disabled:opacity-60"
            style={{ fontFamily: isAr ? "Cairo" : "Plus Jakarta Sans" }}
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {isAr ? "جاري الحفظ…" : "Saving…"}
              </>
            ) : (
              isAr ? "حفظ المورّد" : "Save vendor"
            )}
          </button>
        </form>

        {/* Registered vendors list */}
        <section className="mt-12" data-testid="vendor-list-section">
          <div className="flex items-center justify-between mb-5">
            <h2
              className={`${isAr ? "font-cairo" : "font-display-en"} text-emerald-deep text-xl font-bold`}
            >
              {isAr ? `المورّدون المسجّلون (${vendors.length})` : `Registered vendors (${vendors.length})`}
            </h2>
          </div>

          {loadingList ? (
            <div className="text-emerald-deep/60 text-sm">{isAr ? "جاري التحميل…" : "Loading…"}</div>
          ) : vendors.length === 0 ? (
            <div className="glass rounded-2xl p-6 text-center text-emerald-deep/60 text-sm">
              {isAr ? "لا يوجد مورّدون بعد. أضف أول واحد!" : "No vendors yet. Add the first one!"}
            </div>
          ) : (
            <div className="grid gap-3">
              {vendors.map((v) => (
                <div key={v.id} className="glass rounded-2xl p-4 flex items-start justify-between gap-3" data-testid={`vendor-item-${v.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-emerald-deep text-base">{v.vendor_name}</div>
                    <div className="text-emerald-deep/70 text-xs mt-1" style={{ fontFamily: isAr ? "Cairo" : "Plus Jakarta Sans" }}>
                      {v.city} · {v.experience_type}
                    </div>
                    {v.description && (
                      <div className="text-emerald-deep/60 text-xs mt-2 line-clamp-2" style={{ fontFamily: isAr ? "Cairo" : "Plus Jakarta Sans" }}>
                        {v.description}
                      </div>
                    )}
                  </div>
                  <a
                    href={`https://wa.me/${(v.phone || "").replace(/[^\d]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold text-emerald hover:text-emerald-deep underline whitespace-nowrap"
                  >
                    {v.phone}
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-emerald-deep font-bold text-sm mb-2" style={{ fontFamily: "Cairo" }}>
        {label}
      </span>
      {children}
    </label>
  );
}
